import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { NexusAgent } from '../../../../domain/agents/models/Agent';
import { useProviderServices } from '../../../../app/providers/AppServicesContext';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { acquireNexusApiAccessTokenAsync } from '../../../../services/auth/AuthService';
import { setAgents } from '../../../../redux/features/agents/agentsSlice';
import { buildAvatarSrc } from '../../../utils/AvatarUtils';
import { isNexusAgent } from '../../../utils/AgentUtils';
import { alerts } from '../../../../shared/ui/Alerts';

export interface ManageAgentItem {
    agent: NexusAgent;
    isSoftDeleted: boolean;
    avatarSrc: string;
}

export interface UseManageAgentsPageVm {
    isLoading: boolean;
    items: ManageAgentItem[];
    filteredItems: ManageAgentItem[];
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    onRefresh: () => Promise<void>;
    onCreate: () => void;
    onEdit: (agentId: string) => void;
    onSoftDelete: (agentId: string) => Promise<void>;
    onHardDelete: (agentId: string) => Promise<void>;
}

const toManageItem = (agent: NexusAgent): ManageAgentItem => {
    return {
        agent,
        isSoftDeleted: false,
        avatarSrc: buildAvatarSrc(agent.providerType, agent.agentIconUrl),
    };
};

export const useManageAgentsPage = (): UseManageAgentsPageVm => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { instance } = useMsal();
    const providerServices = useProviderServices();

    const agentsById = useAppSelector((state) => state.agents.byId);
    const agentIds = useAppSelector((state) => state.agents.allIds);

    const [isLoading, setIsLoading] = useState(false);
    const [itemsById, setItemsById] = useState<Record<string, ManageAgentItem>>({});
    const [searchQuery, setSearchQuery] = useState('');

    const fallbackItems = useMemo(() => {
        return agentIds
            .map((id) => agentsById[id])
            .filter(isNexusAgent)
            .map((agent) => ({
                agent,
                isSoftDeleted: false,
                avatarSrc: buildAvatarSrc(agent.providerType, agent.agentIconUrl),
            }))
            .sort((left, right) => left.agent.name.localeCompare(right.agent.name));
    }, [agentIds, agentsById]);

    const items = useMemo(() => {
        const localItems = Object.values(itemsById);
        if (localItems.length === 0) {
            return fallbackItems;
        }

        return localItems.sort((left, right) => left.agent.name.localeCompare(right.agent.name));
    }, [fallbackItems, itemsById]);

    const onRefresh = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        try {
            const accessToken = await acquireNexusApiAccessTokenAsync({
                msalInstance: instance,
            });

            const response = await providerServices.nexus.agents.getAgentsAsync(accessToken, true);
            const nextItems = response.agents.map(toManageItem);
            const nextItemsById: Record<string, ManageAgentItem> = {};
            nextItems.forEach((item) => {
                nextItemsById[item.agent.agentId] = item;
            });

            setItemsById(nextItemsById);
            dispatch(setAgents(nextItems.map((item) => item.agent)));
        } catch {
            alerts.error('Failed to load agents. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, instance, providerServices.nexus.agents]);

    useEffect(() => {
        void onRefresh();
    }, [onRefresh]);

    const onCreate = useCallback(() => {
        navigate('/manage-agents/create');
    }, [navigate]);

    const onEdit = useCallback(
        (agentId: string) => {
            navigate(`/manage-agents/edit/${agentId}`);
        },
        [navigate],
    );

    const onSoftDelete = useCallback(
        async (agentId: string): Promise<void> => {
            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({
                    msalInstance: instance,
                });

                await providerServices.nexus.agents.deleteAgentAsync(agentId, accessToken);
                alerts.success('Agent deleted successfully.');
                await onRefresh();
            } catch {
                alerts.error('Failed to delete agent. Please try again.');
            }
        },
        [instance, onRefresh, providerServices.nexus.agents],
    );

    const onHardDelete = useCallback(
        async (agentId: string): Promise<void> => {
            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({
                    msalInstance: instance,
                });

                await providerServices.nexus.agents.hardDeleteAgentAsync(agentId, accessToken);
                alerts.success('Agent permanently deleted.');
                await onRefresh();
            } catch {
                alerts.error('Failed to permanently delete agent. Please try again.');
            }
        },
        [instance, onRefresh, providerServices.nexus.agents],
    );

    const filteredItems = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return items;
        }

        return items.filter(({ agent }) => {
            const text = `${agent.name} ${agent.description} ${agent.shortDescription ?? ''}`.toLowerCase();
            return text.includes(query);
        });
    }, [searchQuery, items]);

    return {
        isLoading,
        items,
        filteredItems,
        searchQuery,
        setSearchQuery,
        onRefresh,
        onCreate,
        onEdit,
        onSoftDelete,
        onHardDelete,
    };
};

export default useManageAgentsPage;
